import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { POSTS } from "@/lib/blog";
import { BOOKING_URL } from "@/lib/site";
import { FadeUp, MaskedLine, GoldDot } from "@/components/Reveal";
import Footer from "@/components/Footer";

const NotFound = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-8 bg-ivory px-6 text-center" data-testid="blog-not-found">
    <p className="font-serif text-4xl font-light text-forest">This article is still being written.</p>
    <Link to="/blog" className="rounded-full border border-gold px-8 py-3 font-sans text-xs uppercase tracking-[0.3em] text-walnut transition-colors hover:bg-gold/15">
      Back to the Journal
    </Link>
  </div>
);

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) {
      document.title = `${post.title} — Royal Essence Massage Edmonton`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", post.excerpt);
    }
    return () => {
      document.title = "Royal Essence Massage — Massage Beyond Muscles";
    };
  }, [post]);

  if (!post) return (
    <>
      <NotFound />
      <Footer />
    </>
  );

  return (
    <div className="bg-ivory font-sans text-walnut" data-testid="blog-post-page">
      <main>
        <section className="bg-forest pb-20 pt-36 text-ivory lg:pt-44">
          <div className="mx-auto max-w-4xl px-6">
            <FadeUp className="mb-10 flex flex-wrap items-center gap-4">
              <Link to="/blog" className="font-sans text-[11px] font-medium uppercase tracking-[0.35em] text-ivory/60 transition-colors hover:text-gold" data-testid="back-to-blog">
                ← Journal
              </Link>
              <GoldDot />
              <span className="font-sans text-[11px] font-medium uppercase tracking-[0.35em] text-gold">
                {post.category} · {post.read}
              </span>
            </FadeUp>
            <h1 className="font-serif text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl" data-testid="blog-post-title">
              <MaskedLine delay={0.15}>{post.title}</MaskedLine>
            </h1>
          </div>
        </section>

        <div className="mx-auto max-w-4xl px-6 py-16">
          <FadeUp delay={0.1}>
            <div className="blob-shape overflow-hidden">
              <img
                src={post.img}
                alt={post.title}
                className="aspect-[16/8] w-full object-cover"
                loading="lazy"
                data-testid="blog-post-image"
              />
            </div>
          </FadeUp>

          <article className="mt-16 space-y-14" data-testid="blog-post-body">
            {post.body.map((block, i) => (
              <FadeUp key={i} delay={0.05 * i}>
                <section>
                  {block.h && (
                    <h2 className="mb-6 font-serif text-3xl font-light tracking-wide text-forest">
                      {block.h}
                    </h2>
                  )}
                  {block.p.map((para, j) => (
                    <p key={j} className="mb-5 font-sans text-base font-light leading-[1.9] tracking-wide text-walnut/90 last:mb-0">
                      {para}
                    </p>
                  ))}
                </section>
              </FadeUp>
            ))}

            {post.note && (
              <FadeUp>
                <aside className="flex gap-5 rounded-sm bg-sand/50 p-8" data-testid="blog-post-note">
                  <GoldDot className="mt-2 shrink-0" />
                  <p className="font-sans text-sm font-light leading-relaxed tracking-wide text-walnut/85">
                    <span className="font-medium text-forest">A careful note. </span>
                    {post.note}
                  </p>
                </aside>
              </FadeUp>
            )}
          </article>

          <FadeUp>
            <div className="mt-20 flex flex-col items-center gap-8 border-t border-walnut/30 pt-16 text-center">
              <p className="max-w-md font-serif text-3xl font-light italic leading-snug text-forest">
                Ready to give your body this hour?
              </p>
              <div className="flex flex-wrap items-center justify-center gap-8">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 rounded-full border border-gold px-9 py-4 font-sans text-xs font-medium uppercase tracking-[0.3em] text-walnut transition-colors duration-500 hover:bg-gold/15"
                  data-testid="blog-book-button"
                >
                  Book a session in Edmonton
                  <span className="h-[5px] w-[5px] rounded-full bg-gold transition-transform duration-500 group-hover:translate-x-1" />
                </a>
                <Link
                  to="/services"
                  className="font-sans text-xs font-medium uppercase tracking-[0.3em] text-walnut/80 transition-colors hover:text-gold"
                  data-testid="blog-services-link"
                >
                  See all services
                </Link>
              </div>
            </div>
          </FadeUp>

          <div className="mt-20 grid gap-6 md:grid-cols-2">
            {others.map((o) => (
              <FadeUp key={o.slug}>
                <Link
                  to={`/blog/${o.slug}`}
                  className="group block border border-walnut/30 p-8 transition-colors duration-500 hover:border-gold/80 hover:bg-sand/30"
                  data-testid={`blog-related-${o.slug}`}
                >
                  <p className="mb-4 font-sans text-[10px] font-medium uppercase tracking-[0.3em] text-gold">
                    {o.read}
                  </p>
                  <p className="font-serif text-2xl font-light leading-snug text-forest group-hover:text-walnut">
                    {o.title}
                  </p>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
